import React, { useState } from 'react';
import { 
  MobileStepContainer, 
  MobileStepSection, 
  MobileFormItem, 
  MobileActionButton 
} from '@/components/onboarding/MobileStepContainer';
import { FileUp, FileText, Book, Plus, Trash2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface ContentStepMobileProps {
  onNext: (data: Record<string, any>) => void;
  data?: Record<string, any>;
  isLastStep?: boolean;
}

export const ContentStepMobile: React.FC<ContentStepMobileProps> = ({ 
  onNext, 
  data = {},
  isLastStep = false 
}) => {
  const [activeTab, setActiveTab] = useState('faq');
  const [faqs, setFaqs] = useState(data.faqs || [
    { question: 'Como funciona o serviço?', answer: 'Nosso serviço funciona integrando WhatsApp com IA para automatizar atendimentos.' },
    { question: 'Quais os horários de atendimento?', answer: 'Nosso atendimento funciona 24/7 via assistente virtual e em horário comercial com atendentes humanos.' }
  ]);
  const [aboutText, setAboutText] = useState(data.about_text || '');
  const [uploadedFiles, setUploadedFiles] = useState(data.uploaded_files || [
    { name: 'Catálogo de Produtos.pdf', size: '2.4 MB', uploaded: new Date().toISOString() },
    { name: 'Manual de Serviços.docx', size: '1.8 MB', uploaded: new Date().toISOString() }
  ]);

  const handleAddFaq = () => {
    setFaqs([...faqs, { question: '', answer: '' }]);
  };

  const handleRemoveFaq = (index: number) => {
    const newFaqs = [...faqs];
    newFaqs.splice(index, 1);
    setFaqs(newFaqs);
  };

  const handleFaqChange = (index: number, field: 'question' | 'answer', value: string) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  const handleUploadFile = () => {
    // Simulação de upload - em produção seria integrado com um serviço de upload
    const mockFile = {
      name: `Documento-${Math.floor(Math.random() * 1000)}.pdf`,
      size: `${(Math.random() * 5).toFixed(1)} MB`,
      uploaded: new Date().toISOString()
    };
    setUploadedFiles([...uploadedFiles, mockFile]);
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  };

  const handleContinue = () => {
    onNext({
      faqs,
      about_text: aboutText,
      uploaded_files: uploadedFiles,
      configured: true
    });
  };

  return (
    <MobileStepContainer>
      <MobileStepSection className="bg-primary/5 border-primary/20">
        <div className="flex items-center gap-3">
          <Book className="h-10 w-10 text-primary flex-shrink-0" />
          <div>
            <h3 className="font-medium text-base">Base de Conhecimento</h3>
            <p className="text-xs text-muted-foreground">
              Forneça informações para o assistente responder às dúvidas dos clientes com precisão.
            </p>
          </div>
        </div>
      </MobileStepSection>

      <MobileStepSection>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="faq" className="text-xs">Perguntas</TabsTrigger>
            <TabsTrigger value="about" className="text-xs">Sobre</TabsTrigger>
            <TabsTrigger value="documents" className="text-xs">Documentos</TabsTrigger>
          </TabsList>
          
          <TabsContent value="faq" className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="p-3 border rounded-md bg-white">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Pergunta {index + 1}</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleRemoveFaq(index)}
                    className="h-7 w-7 p-0 text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                <Input
                  value={faq.question}
                  onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                  placeholder="Digite a pergunta"
                  className="mb-2"
                />

                <Textarea
                  value={faq.answer}
                  onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                  placeholder="Digite a resposta"
                  rows={2}
                />
              </div>
            ))}

            <Button 
              variant="outline" 
              onClick={handleAddFaq} 
              size="sm"
              className="w-full text-xs h-9"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar Pergunta
            </Button>
          </TabsContent>
          
          <TabsContent value="about" className="space-y-3">
            <MobileFormItem label="Informações sobre a empresa">
              <Textarea
                value={aboutText}
                onChange={(e) => setAboutText(e.target.value)}
                placeholder="Descreva sua empresa, produtos, serviços, missão, visão e valores..."
                rows={6}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Estas informações ajudarão o assistente a entender melhor sua empresa.
              </p>
            </MobileFormItem>
          </TabsContent>
          
          <TabsContent value="documents" className="space-y-3">
            <div 
              className="p-4 border border-dashed rounded-md text-center hover:bg-accent/20 active:bg-accent/30 transition-colors"
              onClick={handleUploadFile}
            >
              <FileUp className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium">Toque para adicionar arquivos</p>
              <p className="text-xs text-muted-foreground">
                PDF, DOCX, TXT (máx. 10MB)
              </p>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Documentos Carregados</h4>
                <div className="space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 border rounded-md bg-white">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="overflow-hidden">
                          <p className="text-xs font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{file.size}</p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemoveFile(index)}
                        className="h-7 w-7 p-0 text-destructive flex-shrink-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </MobileStepSection>

      <MobileStepSection>
        <MobileActionButton 
          onClick={handleContinue}
          className="mt-2"
        >
          Salvar e Continuar
        </MobileActionButton>
      </MobileStepSection>
    </MobileStepContainer>
  );
}; 